import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then(({ data }) => { setInvite(data); setName(data.prefill?.name || ""); })
      .catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/invite/accept", { token, name, password });
      if (data.needs_login) {
        toast.success(data.message || "Added to your account. Please log in.");
        navigate("/login");
      } else if (data.needs_verification) {
        toast.success("Account created. Verify your email.");
        navigate("/verify-email", { state: { email: data.email } });
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (error) return <AuthShell title="Invitation"><p className="text-center text-sm text-rose-600" data-testid="invite-error">{error}</p></AuthShell>;
  if (!invite) return <AuthShell title="Invitation"><p className="text-center text-sm text-slate-500">Loading…</p></AuthShell>;

  const roleLabel = invite.role === "super_admin" ? "Super Admin" : invite.role === "org_admin" ? "Organisation Admin" : "Employee";

  return (
    <AuthShell title="Accept invitation" subtitle={`${roleLabel}${invite.org_name ? " · " + invite.org_name : ""}`}>
      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <div>
          <Label>Email</Label>
          <Input value={invite.email} disabled className="mt-1.5" data-testid="invite-email" />
        </div>
        {invite.has_account ? (
          <p className="text-sm text-slate-600">You already have an account with this email. Accepting will add this access to your existing account.</p>
        ) : (
          <>
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="invite-name" />
            </div>
            <div>
              <Label htmlFor="pw">Create password</Label>
              <Input id="pw" type="password" required minLength={8} value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="Min. 8 characters" data-testid="invite-password" />
            </div>
          </>
        )}
        <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">
          {loading ? "Please wait…" : invite.has_account ? "Add access" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
